import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { categories, articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <section className="pt-24 pb-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-6">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">文章未找到</h1>
            <p className="text-foreground-500 mb-6">你访问的文章可能已被移除或链接有误</p>
            <Link to="/" className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);

  const contentHtml = article.content
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^- \*\*(.+?)\*\*：(.+)$/gm, '<li><strong>$1</strong>：$2</li>')
    .replace(/^- \*\*(.+?)\*\*$/gm, '<li><strong>$1</strong></li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(\d+)\. \*\*(.+?)\*\*：(.+)$/gm, '<li><strong>$2</strong>：$3</li>')
    .replace(/(\d+)\. \*\*(.+?)\*\*$/gm, '<li><strong>$2</strong></li>')
    .replace(/(\d+)\. (.+)$/gm, '<li>$2</li>')
    .replace(/^(.+)$/gm, (line: string) => {
      if (line.startsWith('<h') || line.startsWith('<li') || line.startsWith('<p') || line.trim() === '') return line;
      return `<p>${line}</p>`;
    })
    .replace(/<\/li>\s*<li>/g, '</li><li>')
    .replace(/((?:<li>.*<\/li>\s*)+)/g, '<ul>$1</ul>')
    .replace(/<\/ul>\s*<ul>/g, '');

  return (
    <Layout>
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-8">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
              {category?.name || article.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {category?.name}
              </span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 pb-8 border-b border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-3-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.authorTitle}</p>
              </div>
            </div>
          </header>

          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-sm text-foreground-500 mb-2">
              <strong className="text-foreground-700">免责声明：</strong>
              本文内容仅供参考和教育目的，不能替代专业医疗建议。如有健康疑虑，请咨询执业医师。
            </p>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-2xl p-6 border border-background-200/70 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-3 text-xs text-foreground-500">
                    <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                      {category?.name}
                    </span>
                    <span>{ra.readTime}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ra.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {ra.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}